import { useEffect, useState } from "react";
import {
  arrangeWidgets,
  breakpoints,
  createLayoutConfiguration,
  getBreakpoint,
  getLayoutConfigFromLS,
  validateLayout,
  widgetIdSplitSymbol,
} from "../gridLayoutConfigurations";
import { Layout } from "react-grid-layout";

interface Params {
  /** Dashboard name; eg. `sales` */
  dashboard: string;
  /**
   * Dashboard widget;
   * eg. `orders_and_sales` under `sales` dashboard
   * */
  dashboardWidget: string;
  /** List of analytics widgets */
  analyticsWidgets: { url: string; type: string }[];
  staffId: number | null;
}

type LayoutConfig = {
  [breakpoint: string]: Layout[];
};

/**
 * React hook that determines layout configuration for widgets at A-level
 * <br/>
 * ### Logic for grid layout in A-level
 * 1. Get layout configuration from localStorage using staff_id scoping
 * 2. If the layout config does not exist or does not exist for some widgets,
 *    create a new layout based on widget's visual type
 * 3. Validate layout to prevent overlaps and ensure proper positioning
 * 4. Apply widget arrangement algorithm for optimal packing
 */
const useAnalyticsDashboardGridLayout = ({
  analyticsWidgets,
  dashboard,
  dashboardWidget,
  staffId = 0,
}: Params) => {
  const [gridLayoutConfiguration, setGridLayoutConfiguration] =
    useState<LayoutConfig | null>(null);

  const saveLayoutToLS = (layout: Layout[]) => {
    try {
      const currentBreakpoint = getBreakpoint(window.innerWidth);
      const storageKey = `staff_${staffId}.layout.${dashboard}.${dashboardWidget}`;

      // Get existing layout configuration
      let existingLayout: LayoutConfig = {};
      try {
        const stored = localStorage.getItem(storageKey);
        existingLayout = stored ? JSON.parse(stored) : {};
      } catch (e) {
        console.warn("Failed to parse existing layout, creating new one");
        existingLayout = {};
      }

      // Validate layout to prevent overlaps
      const columns = require("../gridLayoutConfigurations").columns;
      const validatedLayout = validateLayout(
        layout,
        columns[currentBreakpoint]
      );

      // Update layout for current breakpoint
      const updatedLayout = {
        ...existingLayout,
        [currentBreakpoint]: validatedLayout,
      };

      localStorage.setItem(storageKey, JSON.stringify(updatedLayout));
    } catch (e) {
      console.error("Failed to save analytics layout to localStorage:", e);
    }
  };

  const getLayoutConfig = (
    dashboardName: string,
    dashboardWidgetName: string
  ): LayoutConfig | null => {
    return getLayoutConfigFromLS(
      `staff_${staffId}.layout.${dashboardName}.${dashboardWidgetName}`
    );
  };

  useEffect(() => {
    if (!dashboard || !dashboardWidget || !analyticsWidgets.length) {
      setGridLayoutConfiguration(null);
      return;
    }

    try {
      const currentBreakpoint = getBreakpoint(window.innerWidth);

      // Create widget identifiers
      const widgetIdentifiers = analyticsWidgets.map(
        (d) => `${d.url}${widgetIdSplitSymbol}${d.type}`
      );

      // Create default layout configuration
      const defaultGridLayout = arrangeWidgets(
        createLayoutConfiguration({
          bp: breakpoints,
          items: widgetIdentifiers,
        })
      );

      // Get saved layout configuration
      const savedLayoutConfig = getLayoutConfig(dashboard, dashboardWidget);

      let finalLayoutConfig = defaultGridLayout;

      if (savedLayoutConfig) {
        try {
          // Check if saved layout matches current widgets
          const savedWidgetIds =
            savedLayoutConfig[currentBreakpoint]?.map((w) => w.i) || [];
          const currentWidgetIds = widgetIdentifiers;

          // If widget sets match, use saved layout, otherwise use default
          if (
            JSON.stringify(savedWidgetIds.sort()) ===
            JSON.stringify(currentWidgetIds.sort())
          ) {
            finalLayoutConfig = savedLayoutConfig;
          } else {
            console.info(
              "Analytics widget configuration changed, using default layout"
            );
          }
        } catch (e) {
          console.warn(
            "Error processing saved analytics layout, falling back to default"
          );
        }
      }

      // Validate final layout to prevent overlaps
      if (finalLayoutConfig[currentBreakpoint]) {
        const columns = require("../gridLayoutConfigurations").columns;
        finalLayoutConfig[currentBreakpoint] = validateLayout(
          finalLayoutConfig[currentBreakpoint],
          columns[currentBreakpoint]
        );
      }

      setGridLayoutConfiguration(finalLayoutConfig);
    } catch (e) {
      console.error("Error initializing analytics grid layout:", e);
      setGridLayoutConfiguration(null);
    }
  }, [staffId, dashboard, dashboardWidget, JSON.stringify(analyticsWidgets)]);

  return { gridLayoutConfiguration, saveLayoutToLS };
};

export { useAnalyticsDashboardGridLayout };
